import React, { useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Trans, useTranslation } from "react-i18next";
import { DataView } from "primereact/dataview";
import "./VesselFinder.css";
import { finderHeader, vesselTemplate } from "./VesselFinderTemplates";
import { VesselFinderResponse } from "../types/VesselFinderResponse";
import { InstantQuotationVessel } from "../types/InstantQuotationVessel";
import { VesselFinderProps } from "../types/VesselFinderProps";
import { useDebounceCallback } from "./useDebounceCallback";

export const VesselFinder = (props: VesselFinderProps) => {
    const { onClose, onSelect, token, url, visible, commonOnly } = props;

    const { t } = useTranslation("common");

    const [vesselSearch, setVesselSearch] = useState<string>("");
    const [searchResults, setSearchResults] = useState<
        InstantQuotationVessel[] | undefined
    >(undefined);
    const [resultsLoading, setResultsLoading] = useState<boolean>(false);

    const latestRequestRef = useRef<number>(0); // To keep track of the latest request

    const toggleModal = () => {
        latestRequestRef.current = 0;
        setResultsLoading(false);
        onClose();
    };

    const fetchVessels = async (
        searchValue: string,
        requestId: number,
        commonOnly: boolean
    ) => {
        if (!searchValue) {
            setSearchResults(undefined);
            return;
        }

        setResultsLoading(true);
        const response = await fetch(
            `${url}?search=${searchValue}&common=${commonOnly}`,
            {
                headers: {
                    Authorization: "Bearer " + token
                }
            }
        );
        const data: VesselFinderResponse = await response.json();
        const results = data.ships;

        if (requestId === latestRequestRef.current) {
            setSearchResults(results);
            setResultsLoading(false);
        }
    };

    const debounced = useDebounceCallback((value: string) => {
        const currentRequestId = Date.now();
        latestRequestRef.current = currentRequestId;
        setVesselSearch(value);
        fetchVessels(value, currentRequestId, commonOnly);
    }, 500);

    const handleVesselSearchChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (debounced.isPending()) {
            debounced.cancel();
        }

        debounced(event.target.value);
    };

    const handleSelectVessel = (vessel: InstantQuotationVessel) => {
        latestRequestRef.current = 0;
        setResultsLoading(false);
        onSelect(vessel);
        onClose();
    };

    const resultsCountMessage = () => {
        if (searchResults === undefined || searchResults.length === 0) {
            return <></>;
        }

        return (
            <div>
                <span id="search-results-count">
                    {`${
                        searchResults.length > 10 ? "10+" : searchResults.length
                    } ${
                        searchResults.length === 1
                            ? t("instantQuotation.oneResult")
                            : t("instantQuotation.multipleResults")
                    }`}
                </span>
            </div>
        );
    };

    const noRecents = () => {
        if (
            vesselSearch &&
            searchResults !== undefined &&
            searchResults.length === 0
        ) {
            return (
                <div className="flex justify-content-center align-items-center">
                    <span className="search-message">
                        <Trans
                            i18nKey={"instantQuotation.noVesselsFound"}
                            components={[
                                <a
                                    content="Mail link for Idwal Enquiries"
                                    className="summary-link"
                                    key="contact-us"
                                    href="mailto:enquiries@idwalmarine.com"
                                />
                            ]}
                        ></Trans>
                    </span>
                </div>
            );
        }

        return <></>;
    };

    return (
        <Dialog
            draggable={false}
            header={finderHeader(handleVesselSearchChange, vesselSearch, t)}
            visible={visible}
            style={{ width: "42.5rem" }}
            onHide={toggleModal}
        >
            {searchResults !== undefined && searchResults.length > 0 ? (
                <div>
                    {resultsCountMessage()}
                    <DataView
                        loading={resultsLoading}
                        emptyMessage={
                            t("instantQuotation.noVesselsFound") +
                            ` "${vesselSearch}"`
                        }
                        value={searchResults}
                        layout="list"
                        itemTemplate={(data: InstantQuotationVessel) =>
                            vesselTemplate(data, handleSelectVessel, t)
                        }
                    />
                </div>
            ) : null}
            {resultsLoading && (
                <div className="flex justify-content-center align-items-center">
                    <i
                        className="pi pi-spin pi-spinner"
                        style={{ fontSize: "2rem" }}
                    />
                </div>
            )}
            {noRecents()}
        </Dialog>
    );
};
